import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useProfileRoutesConditions, useProfileRoutesRequired } from '@pages/Profile/ProfileRoutes'

import { useSessionEntity } from '@context/EntityContext'

import { AddFileIcon } from '@ui/icons/AddFile.icon'
import { CardIcon } from '@ui/icons/Card.icon'
import { FileIcon } from '@ui/icons/File.icon'
import { LogOutIcon } from '@ui/icons/LogOut.icon'
import { MarketDataClassificationIcon } from '@ui/icons/MarketDataClassification.icon'
import { MobileIcon } from '@ui/icons/Mobile.icon'
import { SettingIcon } from '@ui/icons/Setting.icon'
import { ShieldIcon } from '@ui/icons/Shield.icon'
import { UserGroupIcon } from '@ui/icons/UserGroup.icon'
import { UserSquareIcon } from '@ui/icons/UserSquare.icon'

import { TickmillCompaniesEnum } from '@utils/companyName.utils'
import { CommonPaths } from '@utils/path'

import { useLanguageMenu } from '../../menuSections/topLevelMenus/useLanguageMenu'
import { MenuSection } from '../../menuSections/useMenuSections'

/**
 * topbar profile dropdown items for Setting 3 (LayoutFull).
 *
 * Based on ENTITY:
 * - SC: Personal Details, Preferences, Notifications, Bank Accounts, Phone Numbers, Documents, Security Settings, W-8BEN-e Form, Language, SignOut
 * - UK CFD/EU: + Client Classification
 * - UK ETD: + Market Data Classification
 * lang is a submenu (2nd level) inside the profile dropdown per WCA-6073
 */
export const useProfileMenuItems = (): MenuSection[] => {
  const { t } = useTranslation()
  const entity = useSessionEntity()

  const {
    isClientClassificationVisible,
    isTaxFormVisible,
    isETaxFormVisible,
    isMarketDataClassificationVisible,
    isBankAccountVisible,
    isDocManagementVisible,
    isSumsubVisible,
  } = useProfileRoutesConditions()

  const { shouldVerifyPhoneNumber, documentManagement } = useProfileRoutesRequired()

  const languageMenu = useLanguageMenu()

  const isUK = entity === TickmillCompaniesEnum.TICKMILL_UK
  const isEU = entity === TickmillCompaniesEnum.TICKMILL_EU

  return useMemo(() => {
    const items: MenuSection[] = [
      {
        id: 'profile-personal-details',
        label: t('Profile.Personal Details'),
        path: '/profile/personal-info',
        icon: UserSquareIcon,
      },
      {
        id: 'profile-bank-accounts',
        label: t('Profile.Bank Accounts'),
        path: '/profile/bank-accounts',
        condition: isBankAccountVisible,
        icon: CardIcon,
      },
      {
        id: 'profile-phone-numbers',
        label: t('Profile.Phone Numbers'),
        path: '/profile/phone-numbers',
        alertActive: shouldVerifyPhoneNumber,
        icon: MobileIcon,
      },
      {
        id: 'profile-documents',
        label: t('Profile.Documents'),
        path: isSumsubVisible ? CommonPaths.Sumsub : CommonPaths.DocumentManagement,
        alertActive: documentManagement,
        icon: FileIcon,
        condition: isDocManagementVisible || isSumsubVisible,
      },
      {
        id: 'profile-preferences',
        label: t('Profile.Preferences'),
        path: '/profile/account-settings',
        icon: SettingIcon,
      },
      {
        id: 'profile-security-settings',
        label: t('Profile.Security Settings'),
        path: '/profile/security-settings',
        icon: ShieldIcon,
      },
      {
        id: 'profile-client-classification',
        label: t('Profile.Client Classification'),
        path: '/profile/client-classification',
        icon: UserGroupIcon,
        condition: isClientClassificationVisible && (isUK || isEU),
      },
      {
        id: 'profile-market-data-classification',
        label: t('Profile.Market Data Classification'),
        icon: MarketDataClassificationIcon,
        path: '/profile/market-data-classification',
        condition: isMarketDataClassificationVisible,
      },
      {
        id: 'profile-tax-form',
        label: t('Profile.tax form'),
        path: '/profile/tax-form',
        icon: AddFileIcon,
        condition: isTaxFormVisible,
      },
      {
        id: 'profile-e-tax-form',
        label: t('Profile.W-8BEN-e Form'),
        icon: AddFileIcon,
        path: '/profile/e-tax-form',
        condition: isETaxFormVisible,
      },
      // Lang as submenu (2nd level) — WCA-6073
      {
        ...languageMenu,
        id: 'profile-language',
      },
      // Logout — always last
      {
        id: 'profile-signout',
        label: t('Profile.SignOut'),
        icon: LogOutIcon,
        path: '/logout',
      },
    ]

    return items.filter((item) => item.condition !== false)
  }, [
    t,
    shouldVerifyPhoneNumber,
    documentManagement,
    isBankAccountVisible,
    isDocManagementVisible,
    isSumsubVisible,
    isClientClassificationVisible,
    isMarketDataClassificationVisible,
    isTaxFormVisible,
    isETaxFormVisible,
    isUK,
    isEU,
    languageMenu,
  ])
}
